import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Container,
  Flex,
  Grid,
  HStack,
  VStack,
  Text,
  Heading,
  Button,
  Icon,
  Image,
  Spinner,
  Tooltip,
  Avatar,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
  Link,
} from "@chakra-ui/react";
import {
  IoCalendarOutline,
  IoPeopleOutline,
  IoLocationOutline,
  IoPricetagOutline,
  IoAddOutline,
  IoCheckmarkCircleOutline,
  IoEllipseOutline,
  IoAlertCircleOutline,
  IoStorefrontOutline,
  IoImagesOutline,
  IoCreateOutline,
  IoArrowForwardOutline,
  IoFlagOutline,
  IoCheckmarkOutline,
  IoCloseOutline,
  IoTimeOutline,
  IoLockClosedOutline,
  IoMailOutline,
  IoShieldCheckmarkOutline,
  IoHourglassOutline,
  IoCallOutline,
  IoPersonOutline,
  IoBarChartOutline,
} from "react-icons/io5";
import { motion } from "framer-motion";
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebase-config";
import { CreateEventModal } from "../Components/Modals";
import { EventDashboardModal } from "../Components/Modals/EventDashboardModal";
import { respondToVenueRequest } from "../FBFunctions/eventFunctions";
import { stripHtml } from "../Components/Structure/RichTextEditor";
import { nonHostAttendeeCount, summarizeEventMoney } from "../lib/eventMoney";

const MotionBox = motion(Box);

// ─── Status derived from the event's start/end time ────────────────────────
const STATUS_CONFIG = {
  upcoming: { label: "Upcoming", color: "blue", hex: "#508cf7", icon: IoCalendarOutline },
  live: { label: "Live Now", color: "green", hex: "#22c55e", icon: IoEllipseOutline },
  past: { label: "Past", color: "gray", hex: "#9ca3af", icon: IoCheckmarkCircleOutline },
};

const ALL_STATUSES = Object.entries(STATUS_CONFIG).map(([value, cfg]) => ({ value, ...cfg }));

// ─── Approval buckets (only relevant for the venue owner's perspective) ──
const APPROVAL_CONFIG = {
  pending_approval: {
    label: "Pending Approval",
    color: "orange",
    hex: "#f59e0b",
    icon: IoTimeOutline,
    match: (e, uid) =>
      e.venueOwnerUid === uid && e.host !== uid && e.venueApprovalStatus === "pending",
  },
  approved: {
    label: "Approved",
    color: "green",
    hex: "#22c55e",
    icon: IoCheckmarkOutline,
    match: (e, uid) =>
      e.venueOwnerUid === uid && e.host !== uid && e.venueApprovalStatus === "approved",
  },
  rejected: {
    label: "Rejected",
    color: "red",
    hex: "#ef4444",
    icon: IoCloseOutline,
    match: (e, uid) =>
      e.venueOwnerUid === uid && e.host !== uid && e.venueApprovalStatus === "rejected",
  },
};

const APPROVAL_KEYS = Object.keys(APPROVAL_CONFIG);

const toDate = (ts) => (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null);

const getEventStatus = (e) => {
  const now = new Date();
  const start = toDate(e.startTime);
  const end = toDate(e.endTime);
  if (!start) return "upcoming";
  if (end && now > end) return "past";
  if (now >= start && (!end || now <= end)) return "live";
  return "upcoming";
};

// ─── Helpers ──────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
};

const fmtTime = (ts) => {
  const d = toDate(ts);
  if (!d) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const shortEventId = (id) => (id || "").replace(/[[\]#]/g, "");

// ─── Producer confirmation banner ──────────────────────────────────────────
// Tells the event producer (the host who created the event) whether their
// event is actually going to happen. There are four meaningful states:
//   1. CONFIRMED  — venue is locked in (or it's their own venue) AND ticket
//                   sales already cover the partner-venue minimum.
//   2. ALMOST     — venue approved, but they still need a few more tickets to
//                   clear the partner-venue minimum and stop the auto-cancel.
//   3. PENDING    — partner venue hasn't approved yet.
//   4. DECLINED   — partner venue rejected the event.
// Past events get no banner (whatever happened, happened).
const getProducerConfirmation = (event, status) => {
  if (status === "past") return null;

  const isPartnerVenue = !!event.partnerVenueId;
  const approval = event.venueApprovalStatus;
  const ticketPrice = Number(event.cost) || 0;
  const minRevenue = Number(event.venueMinimumRevenue) || 0;
  const minTickets =
    isPartnerVenue && minRevenue > 0 && ticketPrice > 0
      ? Math.ceil(minRevenue / ticketPrice)
      : 0;
  // Host doesn't pay for their own venue fee, so they shouldn't count
  // toward the partner-venue minimum either.
  const attendees = nonHostAttendeeCount(event);
  const hitMinimum = minTickets === 0 || attendees >= minTickets;

  if (isPartnerVenue && approval === "rejected") {
    return {
      tone: "red",
      icon: IoCloseOutline,
      title: "Venue declined this event",
      sub: "Reach out to arpan@proximy.ca if you'd like to relocate.",
    };
  }
  if (isPartnerVenue && approval !== "approved") {
    return {
      tone: "orange",
      icon: IoHourglassOutline,
      title: "Waiting on venue approval",
      sub: "We'll notify you the moment the venue responds.",
    };
  }

  if (hitMinimum) {
    return {
      tone: "green",
      icon: IoShieldCheckmarkOutline,
      title: "Confirmed — your event is happening",
      sub: minTickets > 0
        ? `Minimum of ${minTickets} ticket${minTickets === 1 ? "" : "s"} hit.`
        : "Locked in. Promote it and fill the room.",
    };
  }

  const remaining = Math.max(0, minTickets - attendees);
  return {
    tone: "amber",
    icon: IoFlagOutline,
    title: `Almost confirmed — ${remaining} more ticket${remaining === 1 ? "" : "s"}`,
    sub: "Events auto-cancel 5 hours before showtime if the minimum isn't met.",
  };
};

const PRODUCER_BANNER_TONES = {
  green: {
    bg: "green.50",
    border: "green.100",
    iconBg: "green.500",
    iconColor: "white",
    title: "green.800",
    sub: "green.700",
  },
  amber: {
    bg: "yellow.50",
    border: "yellow.200",
    iconBg: "yellow.400",
    iconColor: "white",
    title: "yellow.800",
    sub: "yellow.700",
  },
  orange: {
    bg: "orange.50",
    border: "orange.200",
    iconBg: "orange.400",
    iconColor: "white",
    title: "orange.800",
    sub: "orange.700",
  },
  red: {
    bg: "red.50",
    border: "red.100",
    iconBg: "red.500",
    iconColor: "white",
    title: "red.800",
    sub: "red.700",
  },
};

// ─── Event card ────────────────────────────────────────────────────────────
const EventCard = ({ event, index, currentUid, onView, onEdit, onDashboard, onRespond, responding }) => {
  const status = getEventStatus(event);
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.upcoming;
  const capacity = event.maxMembers || 0;

  // "Hosted at my venue" — user only sees this event because they own
  // the venue it's booked into, not because they created it.
  const isVenueOwnerView =
    !!currentUid &&
    event.host !== currentUid &&
    event.venueOwnerUid === currentUid;

  // Partner venue events have a minimum revenue to confirm. Translate that
  // into a minimum ticket count so we can mark it on the progress bar.
  const ticketPrice = Number(event.cost) || 0;
  const minRevenue = Number(event.venueMinimumRevenue) || 0;
  const minTickets =
    event.partnerVenueId && minRevenue > 0 && ticketPrice > 0
      ? Math.ceil(minRevenue / ticketPrice)
      : 0;
  // Host doesn't pay for their own venue fee — they don't count toward
  // the minimum or the visible attendance progress. Anywhere we read
  // a count of "paying attendees" we use this helper.
  const attendees = nonHostAttendeeCount(event);
  const fillRatio = capacity > 0 ? Math.min(attendees / capacity, 1) : 0;
  const minRatio = capacity > 0 && minTickets > 0 ? Math.min(minTickets / capacity, 1) : 0;
  const hitMinimum = minTickets > 0 && attendees >= minTickets;
  const moneySummary = summarizeEventMoney(event);

  // Status banner shown only on the producer's own card (not the venue
  // owner's view of someone else's event). Tells them whether the event is
  // confirmed, waiting on the venue, or declined.
  const producerStatus = !isVenueOwnerView
    ? getProducerConfirmation(event, status)
    : null;
  const producerTone = producerStatus
    ? PRODUCER_BANNER_TONES[producerStatus.tone]
    : null;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07 }}
      bg="white"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 1px 4px rgba(0,0,0,0.04)"
      cursor="pointer"
      onClick={() => onView(event)}
      _hover={{
        borderColor: "blue.100",
        boxShadow: "0 6px 24px rgba(80,140,247,0.10)",
        transform: "translateY(-3px)",
      }}
      style={{ transition: "all 0.22s ease" }}
    >
      {/* Cover image */}
      {event.imageUrl ? (
        <Box h="140px" position="relative" overflow="hidden">
          <Image src={event.imageUrl} alt={event.name} w="full" h="full" objectFit="cover" />
          {/* Cost chip */}
          <Box position="absolute" top={3} right={3}>
            <HStack
              spacing={1}
              bg="whiteAlpha.900"
              backdropFilter="blur(8px)"
              borderRadius="full"
              px={2.5}
              py={1}
              boxShadow="0 2px 6px rgba(0,0,0,0.12)"
            >
              {event.cost > 0 ? (
                <>
                  <Icon as={IoPricetagOutline} boxSize="11px" color="#508cf7" />
                  <Text fontSize="11px" fontWeight="700" color="gray.800">${event.cost}</Text>
                </>
              ) : (
                <Text fontSize="11px" fontWeight="700" color="#508cf7">Free</Text>
              )}
            </HStack>
          </Box>
          {/* Partner / venue-owner badge */}
          {event.partnerVenueId && (
            <Box position="absolute" top={3} left={3} maxW="calc(100% - 96px)">
              {isVenueOwnerView ? (
                <HStack
                  spacing={1}
                  bg={
                    event.venueApprovalStatus === "approved"
                      ? "green.500"
                      : event.venueApprovalStatus === "rejected"
                        ? "red.500"
                        : "orange.400"
                  }
                  borderRadius="full"
                  px={2}
                  py={1}
                  boxShadow="0 2px 6px rgba(0,0,0,0.18)"
                  maxW="full"
                >
                  <Icon as={IoStorefrontOutline} boxSize="10px" color="white" flexShrink={0} />
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color="white"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    noOfLines={1}
                  >
                    {event.venueApprovalStatus === "approved"
                      ? "At your venue"
                      : event.venueApprovalStatus === "rejected"
                        ? "Rejected"
                        : "Pending your approval"}
                  </Text>
                </HStack>
              ) : (
                <HStack
                  spacing={1}
                  bg="blackAlpha.600"
                  backdropFilter="blur(8px)"
                  borderRadius="full"
                  px={2}
                  py={1}
                >
                  <Icon as={IoStorefrontOutline} boxSize="10px" color="white" />
                  <Text fontSize="10px" fontWeight="600" color="white" textTransform="uppercase" letterSpacing="0.06em">
                    Partner
                  </Text>
                </HStack>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Flex h="72px" bg="gray.50" align="center" justify="center" borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={2} color="gray.300">
            <Icon as={IoImagesOutline} boxSize={4} />
            <Text fontSize="11px" fontWeight="500">No image</Text>
          </HStack>
        </Flex>
      )}

      <Flex>
        <Box w="3px" bg={cfg.hex} flexShrink={0} />

        <Box flex="1" p={{ base: 4, md: 6 }} minW={0}>
          {/* Top row */}
          <Flex justify="space-between" align="flex-start" mb={3} gap={2}>
            <VStack align="flex-start" spacing="2px" flex="1" minW={0}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.08em"
              >
                {fmtDate(event.startTime)}
                {event.startTime && ` • ${fmtTime(event.startTime)}`}
              </Text>
              <Heading
                fontSize="lg"
                fontWeight="700"
                color="gray.800"
                lineHeight="1.25"
                letterSpacing="-0.01em"
                noOfLines={1}
              >
                {event.name || "Untitled event"}
              </Heading>
            </VStack>

            <HStack
              spacing={1}
              bg={`${cfg.color}.50`}
              color={`${cfg.color}.600`}
              borderRadius="full"
              px={2}
              py={1}
              flexShrink={0}
              ml={3}
            >
              <Icon as={cfg.icon} boxSize="10px" />
              <Text fontSize="11px" fontWeight="600">{cfg.label}</Text>
            </HStack>
          </Flex>

          {/* Description — stripped to plain text so card previews
              never render raw HTML tags from the rich-text editor. */}
          {event.description && (() => {
            const preview = stripHtml(event.description);
            if (!preview) return null;
            return (
              <Text fontSize="sm" color="gray.500" noOfLines={2} lineHeight="1.6" mb={4} textAlign="left">
                {preview}
              </Text>
            );
          })()}

          {/* Location */}
          {event.location && (
            <HStack spacing="6px" mb={5} align="flex-start">
              <Icon as={IoLocationOutline} color="gray.300" boxSize="13px" flexShrink={0} mt="2px" />
              <Text fontSize="12px" color="gray.400" noOfLines={1} textAlign="left">
                {event.partnerVenueName || event.location}
              </Text>
            </HStack>
          )}

          {/* Event creator contact — only shown to the venue owner so
              they can reach the producer who made the booking. */}
          {isVenueOwnerView && (() => {
            const h = event.hostData;
            const name = h?.displayName || h?.name || "Event producer";
            const email = h?.email;
            const phone = h?.phoneNumber;
            const hasContact = email || phone;
            return (
              <Box
                mb={5}
                px={3}
                py={2.5}
                bg="gray.50"
                border="1px solid"
                borderColor="gray.100"
                borderRadius="lg"
              >
                <HStack spacing={3} align="center">
                  <Avatar
                    size="sm"
                    name={name}
                    src={h?.avatarUrl || h?.photoURL}
                    flexShrink={0}
                  />
                  <Box flex={1} minW={0}>
                    <HStack spacing={1} mb="1px">
                      <Icon as={IoPersonOutline} color="gray.400" boxSize="11px" />
                      <Text fontSize="11px" fontWeight="700" color="gray.700" noOfLines={1}>
                        {name}
                      </Text>
                    </HStack>
                    {hasContact ? (
                      <HStack spacing={3} flexWrap="wrap">
                        {email && (
                          <HStack spacing={1}>
                            <Icon as={IoMailOutline} color="gray.400" boxSize="11px" />
                            <Link
                              href={`mailto:${email}`}
                              fontSize="11px"
                              color="#508cf7"
                              fontWeight="500"
                              noOfLines={1}
                              _hover={{ textDecoration: "underline" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {email}
                            </Link>
                          </HStack>
                        )}
                        {phone && (
                          <HStack spacing={1}>
                            <Icon as={IoCallOutline} color="gray.400" boxSize="11px" />
                            <Link
                              href={`tel:${phone}`}
                              fontSize="11px"
                              color="#508cf7"
                              fontWeight="500"
                              _hover={{ textDecoration: "underline" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {phone}
                            </Link>
                          </HStack>
                        )}
                      </HStack>
                    ) : (
                      <Text fontSize="10px" color="gray.400">No contact info available</Text>
                    )}
                  </Box>
                  <Text
                    fontSize="9px"
                    fontWeight="700"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    flexShrink={0}
                  >
                    Producer
                  </Text>
                </HStack>
              </Box>
            );
          })()}

          {/* Producer confirmation banner — only shown to the event's own
              producer, never to a venue owner viewing someone else's event. */}
          {producerStatus && producerTone && (
            <HStack
              spacing={3}
              mb={5}
              px={3}
              py={2.5}
              bg={producerTone.bg}
              border="1px solid"
              borderColor={producerTone.border}
              borderRadius="lg"
              align="flex-start"
            >
              <Flex
                w="28px"
                h="28px"
                minW="28px"
                borderRadius="md"
                bg={producerTone.iconBg}
                align="center"
                justify="center"
                flexShrink={0}
                boxShadow="0 1px 3px rgba(0,0,0,0.08)"
              >
                <Icon
                  as={producerStatus.icon}
                  color={producerTone.iconColor}
                  boxSize="14px"
                />
              </Flex>
              <VStack align="flex-start" spacing="1px" flex={1} minW={0}>
                <Text
                  fontSize="12px"
                  fontWeight="700"
                  color={producerTone.title}
                  lineHeight="1.25"
                >
                  {producerStatus.title}
                </Text>
                {producerStatus.sub && (
                  <Text
                    fontSize="11px"
                    color={producerTone.sub}
                    lineHeight="1.4"
                    noOfLines={2}
                    textAlign="left"
                  >
                    {producerStatus.sub}
                  </Text>
                )}
              </VStack>
            </HStack>
          )}

          {/* Revenue panel — producer sees venue/producer/platform
              breakdown, venue owner sees just venue progress against
              their minimum. Skipped entirely for free events. */}
          {(() => {
            const isPaidEvent =
              ticketPrice > 0 || moneySummary.collected > 0;
            if (!isPaidEvent) return null;

            const fmtCents = (cents) => {
              const dollars = (cents || 0) / 100;
              return dollars % 1 === 0
                ? `$${dollars}`
                : `$${dollars.toFixed(2)}`;
            };

            // ── Venue-owner view: just venue progress ─────────────
            if (isVenueOwnerView) {
              if (moneySummary.venueMinimum <= 0) return null;
              return (
                <VStack align="stretch" spacing={1.5} mb={5}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1}>
                      <Icon
                        as={IoStorefrontOutline}
                        color="gray.400"
                        boxSize="13px"
                      />
                      <Text
                        fontSize="11px"
                        color="gray.500"
                        fontWeight="500"
                      >
                        Venue revenue
                      </Text>
                    </HStack>
                    <Text fontSize="11px" fontWeight="700" color="gray.700">
                      {fmtCents(moneySummary.venueCovered)} /{" "}
                      {fmtCents(moneySummary.venueMinimum)}
                    </Text>
                  </Flex>
                  <Box
                    h="6px"
                    bg="gray.100"
                    borderRadius="full"
                    overflow="hidden"
                  >
                    <Box
                      h="full"
                      w={`${moneySummary.venueProgress * 100}%`}
                      bg={
                        moneySummary.venueProgress >= 1
                          ? "green.400"
                          : "#508cf7"
                      }
                      borderRadius="full"
                      style={{ transition: "width 0.3s ease" }}
                    />
                  </Box>
                </VStack>
              );
            }

            // ── Producer view: venue / producer / platform split ──
            const collected = moneySummary.collected;
            const segVenue =
              collected > 0
                ? (moneySummary.venueCovered / collected) * 100
                : 0;
            const segProducer =
              collected > 0
                ? (moneySummary.producerEarned / collected) * 100
                : 0;
            const segPlatform =
              collected > 0
                ? (moneySummary.platformEarned / collected) * 100
                : 0;
            const venueFullyCovered =
              moneySummary.venueMinimum === 0 ||
              moneySummary.venueRemaining === 0;
            // Headline tracks the *active* metric for the producer:
            //   • Below the venue minimum, "Earnings $0" is correct
            //     but uninformative — the producer can see they're
            //     making progress on the venue minimum, so we show
            //     that instead.
            //   • Once covered (or for self-hosted events with no
            //     minimum) we flip to "Earnings $X" — the share of
            //     the take that's actually theirs.
            const showVenueProgress =
              moneySummary.venueMinimum > 0 && !venueFullyCovered;
            const headlineLabel = showVenueProgress
              ? "Venue progress"
              : "Earnings";
            const headlineAmount = showVenueProgress
              ? `${fmtCents(moneySummary.venueCovered)} / ${fmtCents(moneySummary.venueMinimum)}`
              : fmtCents(moneySummary.producerEarned);
            const subtitle = showVenueProgress
              ? "All sales go to the venue until it's covered. After that, 30% goes to you."
              : moneySummary.venueMinimum > 0
              ? `Venue covered. ${fmtCents(moneySummary.producerEarned)} to you so far.`
              : `${fmtCents(moneySummary.producerEarned)} to you so far.`;

            return (
              <VStack align="stretch" spacing={1.5} mb={5}>
                <Flex justify="space-between" align="center">
                  <HStack spacing={1}>
                    <Icon
                      as={IoPricetagOutline}
                      color="gray.400"
                      boxSize="13px"
                    />
                    <Text
                      fontSize="11px"
                      color="gray.500"
                      fontWeight="500"
                    >
                      {headlineLabel}
                    </Text>
                  </HStack>
                  <Text fontSize="11px" fontWeight="700" color="gray.700">
                    {headlineAmount}
                  </Text>
                </Flex>
                {collected > 0 ? (
                  <Flex
                    h="6px"
                    bg="gray.100"
                    borderRadius="full"
                    overflow="hidden"
                  >
                    {segVenue > 0 && (
                      <Tooltip
                        label={`Venue: ${fmtCents(moneySummary.venueCovered)}`}
                        hasArrow
                        placement="top"
                        bg="gray.900"
                        color="white"
                        borderRadius="md"
                        fontSize="10px"
                        px={2}
                        py={1}
                        openDelay={200}
                      >
                        <Box
                          h="full"
                          w={`${segVenue}%`}
                          bg="orange.300"
                          style={{ transition: "width 0.3s ease" }}
                        />
                      </Tooltip>
                    )}
                    {segProducer > 0 && (
                      <Tooltip
                        label={`You: ${fmtCents(moneySummary.producerEarned)}`}
                        hasArrow
                        placement="top"
                        bg="gray.900"
                        color="white"
                        borderRadius="md"
                        fontSize="10px"
                        px={2}
                        py={1}
                        openDelay={200}
                      >
                        <Box
                          h="full"
                          w={`${segProducer}%`}
                          bg="#508cf7"
                          style={{ transition: "width 0.3s ease" }}
                        />
                      </Tooltip>
                    )}
                    {segPlatform > 0 && (
                      <Tooltip
                        label={`Platform: ${fmtCents(moneySummary.platformEarned)}`}
                        hasArrow
                        placement="top"
                        bg="gray.900"
                        color="white"
                        borderRadius="md"
                        fontSize="10px"
                        px={2}
                        py={1}
                        openDelay={200}
                      >
                        <Box
                          h="full"
                          w={`${segPlatform}%`}
                          bg="gray.400"
                          style={{ transition: "width 0.3s ease" }}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                ) : (
                  <Box h="6px" bg="gray.100" borderRadius="full" />
                )}
                <Text
                  fontSize="10px"
                  color="gray.500"
                  lineHeight="1.35"
                  noOfLines={2}
                >
                  {subtitle}
                </Text>
              </VStack>
            );
          })()}

          {/* Attendance bar */}
          {capacity > 0 && (
            <VStack align="stretch" spacing={1.5} mb={5}>
              <Flex justify="space-between" align="center">
                <HStack spacing={1}>
                  <Icon as={IoPeopleOutline} color="gray.400" boxSize="13px" />
                  <Text fontSize="11px" color="gray.500" fontWeight="500">Attendees</Text>
                </HStack>
                <HStack spacing={2}>
                  {minTickets > 0 && (() => {
                    const remaining = Math.max(0, minTickets - attendees);
                    const minTooltipLabel = (
                      <HStack spacing={2} align="flex-start" minW="220px">
                        <Flex
                          w="26px"
                          h="26px"
                          borderRadius="md"
                          bg="whiteAlpha.200"
                          align="center"
                          justify="center"
                          flexShrink={0}
                        >
                          <Icon
                            as={hitMinimum ? IoCheckmarkCircleOutline : IoFlagOutline}
                            color="white"
                            boxSize="13px"
                          />
                        </Flex>
                        <VStack align="flex-start" spacing="2px">
                          <Text
                            fontSize="12px"
                            fontWeight="700"
                            color="white"
                            lineHeight="1.2"
                          >
                            {hitMinimum
                              ? "Partner venue minimum hit"
                              : `${remaining} more ticket${remaining === 1 ? "" : "s"} to confirm`}
                          </Text>
                          <Text
                            fontSize="11px"
                            color="whiteAlpha.800"
                            lineHeight="1.3"
                          >
                            {hitMinimum
                              ? `${minTickets} paid ticket${minTickets === 1 ? "" : "s"} covers the booking.`
                              : `Need ${minTickets} paid ticket${minTickets === 1 ? "" : "s"} to cover the venue's minimum.`}
                          </Text>
                          {!hitMinimum && (
                            <Text
                              fontSize="10px"
                              color="whiteAlpha.600"
                              lineHeight="1.3"
                            >
                              Auto-cancels 5h before start if not met.
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    );

                    return (
                      <Tooltip
                        label={minTooltipLabel}
                        hasArrow
                        placement="top"
                        bg="gray.900"
                        color="white"
                        borderRadius="lg"
                        px={3}
                        py={2}
                        boxShadow="0 14px 30px -10px rgba(15, 23, 42, 0.6)"
                        openDelay={120}
                        shouldWrapChildren
                      >
                        <HStack
                          spacing={1}
                          bg={hitMinimum ? "green.50" : "orange.50"}
                          color={hitMinimum ? "green.600" : "orange.600"}
                          borderRadius="full"
                          px={1.5}
                          py="1px"
                          cursor="help"
                        >
                          <Icon
                            as={hitMinimum ? IoCheckmarkCircleOutline : IoFlagOutline}
                            boxSize="10px"
                          />
                          <Text fontSize="10px" fontWeight="700">
                            min {minTickets}
                          </Text>
                        </HStack>
                      </Tooltip>
                    );
                  })()}
                  <Text fontSize="11px" fontWeight="700" color="gray.700">
                    {attendees} / {capacity}
                  </Text>
                </HStack>
              </Flex>
              <Box h="6px" bg="gray.100" borderRadius="full" overflow="hidden" position="relative">
                {/* Fill */}
                <Box
                  h="full"
                  w={`${fillRatio * 100}%`}
                  bg={fillRatio >= 1 ? "green.400" : hitMinimum ? "green.400" : "#508cf7"}
                  borderRadius="full"
                  style={{ transition: "width 0.3s ease" }}
                />
                {/* Minimum marker — only for partner venue events */}
                {minRatio > 0 && minRatio < 1 && (
                  <Box
                    position="absolute"
                    top="-2px"
                    bottom="-2px"
                    left={`calc(${minRatio * 100}% - 1px)`}
                    w="2px"
                    bg={hitMinimum ? "green.500" : "orange.400"}
                    borderRadius="full"
                    boxShadow={
                      hitMinimum
                        ? "0 0 0 2px rgba(72,187,120,0.2)"
                        : "0 0 0 2px rgba(255,159,28,0.25)"
                    }
                  />
                )}
              </Box>
            </VStack>
          )}

          {/* Footer */}
          <Flex
            justify="space-between"
            align={{ base: "flex-start", sm: "center" }}
            gap={3}
            flexWrap="wrap"
          >
            <VStack spacing={0} align="flex-start" minW={0}>
              <Text fontSize="11px" color="gray.300">
                Created {fmtDate(event.createdAt)}
              </Text>
              <Text fontSize="10px" color="gray.300" fontWeight="500">
                #{shortEventId(event.id || event.docId).slice(0, 6).toUpperCase()}
              </Text>
            </VStack>

            <HStack spacing={2} flexWrap="wrap" justify="flex-start">
              {isVenueOwnerView ? (
                (() => {
                  const status = event.venueApprovalStatus;
                  const isDecided =
                    status === "approved" || status === "rejected";
                  const isApproved = status === "approved";
                  const isRejected = status === "rejected";

                  // Custom rich tooltip — shown only once a decision is locked.
                  // Wrapped in `shouldWrapChildren` because the underlying
                  // Button is `isDisabled`, which would otherwise swallow the
                  // pointer events that Tooltip needs.
                  const lockedTooltipLabel = (
                    <HStack spacing={2} align="flex-start" minW="220px">
                      <Flex
                        w="26px"
                        h="26px"
                        borderRadius="md"
                        bg="whiteAlpha.200"
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <Icon
                          as={IoLockClosedOutline}
                          color="white"
                          boxSize="13px"
                        />
                      </Flex>
                      <VStack align="flex-start" spacing="2px">
                        <Text
                          fontSize="12px"
                          fontWeight="700"
                          color="white"
                          lineHeight="1.2"
                        >
                          Decision locked
                        </Text>
                        <HStack spacing={1} align="center">
                          <Icon
                            as={IoMailOutline}
                            color="whiteAlpha.700"
                            boxSize="11px"
                          />
                          <Text
                            fontSize="11px"
                            color="whiteAlpha.800"
                            lineHeight="1.3"
                          >
                            arpan@proximy.ca
                          </Text>
                        </HStack>
                        <Text
                          fontSize="10px"
                          color="whiteAlpha.600"
                          lineHeight="1.3"
                        >
                          Reach out for any changes.
                        </Text>
                      </VStack>
                    </HStack>
                  );

                  const lockedTooltipProps = {
                    label: lockedTooltipLabel,
                    isDisabled: !isDecided,
                    hasArrow: true,
                    placement: "top",
                    bg: "gray.900",
                    color: "white",
                    borderRadius: "lg",
                    px: 3,
                    py: 2,
                    boxShadow: "0 14px 30px -10px rgba(15, 23, 42, 0.6)",
                    openDelay: 120,
                    shouldWrapChildren: true,
                  };

                  return (
                    <>
                      {/* Approve — locked once any decision has been made */}
                      <Tooltip {...lockedTooltipProps}>
                        <Button
                          size="sm"
                          fontWeight="600"
                          fontSize="12px"
                          borderRadius="full"
                          h="32px"
                          px={3}
                          isLoading={
                            responding?.id === (event.docId || event.id) &&
                            responding?.action === "approved"
                          }
                          isDisabled={isDecided}
                          leftIcon={
                            <Icon as={IoCheckmarkOutline} boxSize="14px" />
                          }
                          bg={isApproved ? "#22c55e" : "white"}
                          color={isApproved ? "white" : "#16a34a"}
                          border="1px solid"
                          borderColor={isApproved ? "#22c55e" : "green.100"}
                          opacity={isRejected ? 0.45 : 1}
                          cursor={isDecided ? "not-allowed" : "pointer"}
                          _hover={
                            isDecided
                              ? {}
                              : {
                                bg: "green.50",
                                borderColor: "#22c55e",
                                transform: "translateY(-1px)",
                              }
                          }
                          _active={isDecided ? {} : { transform: "translateY(0)" }}
                          _disabled={{
                            bg: isApproved ? "#22c55e" : "white",
                            color: isApproved ? "white" : "#16a34a",
                            borderColor: isApproved ? "#22c55e" : "green.100",
                            opacity: isRejected ? 0.45 : 1,
                            cursor: "not-allowed",
                            boxShadow: "none",
                          }}
                          style={{ transition: "all 0.18s ease" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDecided) onRespond(event, "approved");
                          }}
                        >
                          {isApproved ? "Approved" : "Approve"}
                        </Button>
                      </Tooltip>

                      {/* Reject — locked once any decision has been made */}
                      <Tooltip {...lockedTooltipProps}>
                        <Button
                          size="sm"
                          fontWeight="600"
                          fontSize="12px"
                          borderRadius="full"
                          h="32px"
                          px={3}
                          isLoading={
                            responding?.id === (event.docId || event.id) &&
                            responding?.action === "rejected"
                          }
                          isDisabled={isDecided}
                          leftIcon={<Icon as={IoCloseOutline} boxSize="14px" />}
                          bg={isRejected ? "#ef4444" : "white"}
                          color={isRejected ? "white" : "#dc2626"}
                          border="1px solid"
                          borderColor={isRejected ? "#ef4444" : "red.100"}
                          opacity={isApproved ? 0.45 : 1}
                          cursor={isDecided ? "not-allowed" : "pointer"}
                          _hover={
                            isDecided
                              ? {}
                              : {
                                bg: "red.50",
                                borderColor: "#ef4444",
                                transform: "translateY(-1px)",
                              }
                          }
                          _active={isDecided ? {} : { transform: "translateY(0)" }}
                          _disabled={{
                            bg: isRejected ? "#ef4444" : "white",
                            color: isRejected ? "white" : "#dc2626",
                            borderColor: isRejected ? "#ef4444" : "red.100",
                            opacity: isApproved ? 0.45 : 1,
                            cursor: "not-allowed",
                            boxShadow: "none",
                          }}
                          style={{ transition: "all 0.18s ease" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDecided) onRespond(event, "rejected");
                          }}
                        >
                          {isRejected ? "Rejected" : "Reject"}
                        </Button>
                      </Tooltip>
                    </>
                  );
                })()
              ) : (
                <>
                <Button
                  size="sm"
                  variant="outline"
                  color="gray.700"
                  borderColor="gray.200"
                  bg="white"
                  fontWeight="600"
                  fontSize="12px"
                  borderRadius="full"
                  h="32px"
                  px={3}
                  leftIcon={<Icon as={IoBarChartOutline} boxSize="14px" />}
                  _hover={{
                    bg: "gray.50",
                    borderColor: "gray.300",
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  style={{ transition: "all 0.18s ease" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDashboard(event);
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  color="#508cf7"
                  borderColor="blue.100"
                  bg="white"
                  fontWeight="600"
                  fontSize="12px"
                  borderRadius="full"
                  h="32px"
                  px={3}
                  leftIcon={<Icon as={IoCreateOutline} boxSize="14px" />}
                  _hover={{
                    bg: "blue.50",
                    borderColor: "#508cf7",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 10px rgba(80,140,247,0.18)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  style={{ transition: "all 0.18s ease" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(event);
                  }}
                >
                  Edit
                </Button>
                </>
              )}
              <Button
                size="sm"
                fontWeight="600"
                fontSize="12px"
                borderRadius="full"
                h="32px"
                px={3}
                bg="#508cf7"
                color="white"
                rightIcon={<Icon as={IoArrowForwardOutline} boxSize="14px" />}
                _hover={{
                  bg: "#226cf1",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 14px rgba(80,140,247,0.35)",
                }}
                _active={{ transform: "translateY(0)" }}
                style={{ transition: "all 0.18s ease" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onView(event);
                }}
              >
                View
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Flex>
    </MotionBox>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────
export const EventsPage = () => {
  const [user] = useAuthState(auth);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [editingEvent, setEditingEvent] = useState(null);
  const [dashboardEvent, setDashboardEvent] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  const fetchEvents = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Two parallel queries merged client-side:
      //   1. Events the user is hosting (`host == uid`)
      //   2. Events booked into one of the user's partner venues
      //      (`venueOwnerUid == uid`)
      // Dedupe by docId so events that satisfy both (unusual but possible
      // if a host books into their own venue) only appear once.
      const hostQ = query(collection(db, "events"), where("host", "==", user.uid));
      const venueQ = query(
        collection(db, "events"),
        where("venueOwnerUid", "==", user.uid)
      );
      const [hostSnap, venueSnap] = await Promise.all([getDocs(hostQ), getDocs(venueQ)]);

      const byId = new Map();
      [...hostSnap.docs, ...venueSnap.docs].forEach((d) => {
        if (!byId.has(d.id)) byId.set(d.id, { docId: d.id, ...d.data() });
      });
      const rows = Array.from(byId.values());

      // Newest first by createdAt
      rows.sort((a, b) => {
        const da = toDate(a.createdAt)?.getTime() || 0;
        const dbt = toDate(b.createdAt)?.getTime() || 0;
        return dbt - da;
      });

      // For venue-owner view events (host is someone else), batch-fetch
      // each unique host's user doc so the card can show contact info.
      const venueOwnerRows = rows.filter(
        (e) => e.venueOwnerUid === user.uid && e.host && e.host !== user.uid
      );
      const uniqueHostUids = [...new Set(venueOwnerRows.map((e) => e.host))];
      if (uniqueHostUids.length > 0) {
        const hostDocs = await Promise.all(
          uniqueHostUids.map((uid) => getDoc(doc(db, "users", uid)))
        );
        const hostMap = {};
        hostDocs.forEach((snap) => {
          if (snap.exists()) hostMap[snap.id] = snap.data();
        });
        rows.forEach((e) => {
          if (e.host && hostMap[e.host]) e.hostData = hostMap[e.host];
        });
      }

      setEvents(rows);
    } catch (err) {
      toast({
        title: "Failed to load events",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleView = (event) => {
    const id = shortEventId(event.id || event.docId);
    navigate(`/hangout/${id}`);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    onOpen();
  };

  const handleNewEvent = () => {
    setEditingEvent(null);
    onOpen();
  };

  const handleModalClose = () => {
    onClose();
    setEditingEvent(null);
    fetchEvents();
  };

  // Venue owner accept/reject. Tracks both the event id AND the action so
  // the right button shows the spinner even after an optimistic update
  // flips the status (which would otherwise confuse a simple id-only
  // isLoading check).
  const [responding, setResponding] = useState(null); // { id, action } | null

  // Two-step confirm — venue decisions are final, so we always gate the
  // backend call behind an AlertDialog the owner has to explicitly confirm.
  const [confirmAction, setConfirmAction] = useState(null); // { event, action } | null
  const cancelConfirmRef = useRef(null);

  const requestRespond = useCallback(
    (event, action) => {
      const status = event.venueApprovalStatus;
      if (status === "approved" || status === "rejected") {
        toast({
          title: "Decision is locked",
          description:
            "Email arpan@proximy.ca if you need to change your decision.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      setConfirmAction({ event, action });
    },
    [toast]
  );

  const handleRespond = useCallback(
    async (event, action) => {
      const docId = event.docId || event.id;
      setResponding({ id: docId, action });
      const previousStatus = event.venueApprovalStatus;
      setEvents((prev) =>
        prev.map((e) =>
          (e.docId || e.id) === docId ? { ...e, venueApprovalStatus: action } : e
        )
      );
      try {
        await respondToVenueRequest({ eventId: docId, action });
        toast({
          title: action === "approved" ? "Event approved" : "Event rejected",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } catch (err) {
        setEvents((prev) =>
          prev.map((e) =>
            (e.docId || e.id) === docId ? { ...e, venueApprovalStatus: previousStatus } : e
          )
        );
        toast({
          title: "Couldn't save decision",
          description: err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setResponding(null);
      }
    },
    [toast]
  );

  const uid = user?.uid;

  const filtered = (() => {
    if (activeFilter === "all") return events;
    if (APPROVAL_CONFIG[activeFilter]) {
      return events.filter((e) => APPROVAL_CONFIG[activeFilter].match(e, uid));
    }
    return events.filter((e) => getEventStatus(e) === activeFilter);
  })();

  const counts = {
    ...Object.fromEntries(
      Object.keys(STATUS_CONFIG).map((s) => [
        s,
        events.filter((e) => getEventStatus(e) === s).length,
      ])
    ),
    ...Object.fromEntries(
      APPROVAL_KEYS.map((k) => [
        k,
        events.filter((e) => APPROVAL_CONFIG[k].match(e, uid)).length,
      ])
    ),
  };

  return (
    <Box minH="100vh" bg="#f7f8fc">

      {/* ── Page header ── */}
      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" px={4} py={4}>
        <Container maxW="5xl">
          <Flex justify="space-between" align="center" gap={4} flexWrap="wrap">
            <HStack spacing={3} align="center">
              <Box
                w="32px"
                h="32px"
                borderRadius="lg"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoCalendarOutline} boxSize="15px" color="#508cf7" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Heading
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="700"
                  color="gray.800"
                  letterSpacing="-0.01em"
                  lineHeight="1.2"
                >
                  Your Events
                </Heading>
                <Text fontSize="12px" color="gray.400" lineHeight="1.3" textAlign="left">
                  Track your hosted events and manage attendees
                </Text>
              </VStack>

              {/* Compact inline stats */}
              {!loading && events.length > 0 && (
                <HStack
                  spacing={1.5}
                  ml={4}
                  pl={4}
                  borderLeft="1px solid"
                  borderColor="gray.100"
                  display={{ base: "none", md: "flex" }}
                >
                  {[
                    { label: "Total", value: events.length, color: "gray.700" },
                    { label: "Upcoming", value: counts.upcoming, color: "#508cf7" },
                    { label: "Live", value: counts.live, color: "green.500" },
                    { label: "Past", value: counts.past, color: "gray.400" },
                  ].map(({ label, value, color }) => (
                    <HStack
                      key={label}
                      spacing={1.5}
                      px={2.5}
                      py={1}
                      bg="gray.50"
                      borderRadius="full"
                    >
                      <Text fontSize="11px" color="gray.400" fontWeight="500">{label}</Text>
                      <Text fontSize="12px" fontWeight="700" color={color}>{value}</Text>
                    </HStack>
                  ))}
                </HStack>
              )}
            </HStack>

            <Button
              leftIcon={<Icon as={IoAddOutline} boxSize={4} />}
              bg="#508cf7"
              color="white"
              fontWeight="600"
              fontSize="sm"
              borderRadius="xl"
              size="sm"
              px={4}
              _hover={{
                bg: "#226cf1",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(80,140,247,0.35)",
              }}
              style={{ transition: "all 0.18s ease" }}
              onClick={handleNewEvent}
            >
              New Event
            </Button>
          </Flex>

          {/* Mobile-only compact stats */}
          {!loading && events.length > 0 && (
            <HStack
              spacing={1.5}
              mt={3}
              flexWrap="wrap"
              display={{ base: "flex", md: "none" }}
            >
              {[
                { label: "Total", value: events.length, color: "gray.700" },
                { label: "Upcoming", value: counts.upcoming, color: "#508cf7" },
                { label: "Live", value: counts.live, color: "green.500" },
                { label: "Past", value: counts.past, color: "gray.400" },
              ].map(({ label, value, color }) => (
                <HStack key={label} spacing={1.5} px={2.5} py={1} bg="gray.50" borderRadius="full">
                  <Text fontSize="11px" color="gray.400" fontWeight="500">{label}</Text>
                  <Text fontSize="12px" fontWeight="700" color={color}>{value}</Text>
                </HStack>
              ))}
            </HStack>
          )}
        </Container>
      </Box>

      {/* ── Content ── */}
      <Container maxW="5xl" pt={5} pb={16} px={{ base: 3, md: 4 }}>

        {/* Filter pills */}
        {events.length > 0 && (
          <HStack spacing={2} mb={7} flexWrap="wrap">
            <Button
              size="sm"
              borderRadius="full"
              fontWeight="500"
              fontSize="12px"
              px={4}
              bg={activeFilter === "all" ? "#508cf7" : "white"}
              color={activeFilter === "all" ? "white" : "gray.500"}
              border="1px solid"
              borderColor={activeFilter === "all" ? "#508cf7" : "gray.200"}
              _hover={{ borderColor: "#508cf7", color: activeFilter === "all" ? "white" : "#508cf7" }}
              onClick={() => setActiveFilter("all")}
            >
              All  {events.length}
            </Button>
            {ALL_STATUSES.filter((s) => counts[s.value] > 0).map((s) => (
              <Button
                key={s.value}
                size="sm"
                borderRadius="full"
                fontWeight="500"
                fontSize="12px"
                px={4}
                bg={activeFilter === s.value ? `${s.color}.50` : "white"}
                color={activeFilter === s.value ? `${s.color}.600` : "gray.500"}
                border="1px solid"
                borderColor={activeFilter === s.value ? `${s.color}.200` : "gray.200"}
                _hover={{ borderColor: `${s.color}.300`, color: `${s.color}.600` }}
                onClick={() => setActiveFilter(s.value)}
              >
                {s.label}  {counts[s.value]}
              </Button>
            ))}
            {APPROVAL_KEYS.filter((k) => counts[k] > 0).map((k) => {
              const cfg = APPROVAL_CONFIG[k];
              const active = activeFilter === k;
              return (
                <Button
                  key={k}
                  size="sm"
                  borderRadius="full"
                  fontWeight="500"
                  fontSize="12px"
                  px={4}
                  leftIcon={<Icon as={cfg.icon} boxSize="12px" />}
                  bg={active ? `${cfg.color}.50` : "white"}
                  color={active ? `${cfg.color}.700` : "gray.500"}
                  border="1px solid"
                  borderColor={active ? `${cfg.color}.200` : "gray.200"}
                  _hover={{ borderColor: `${cfg.color}.300`, color: `${cfg.color}.700` }}
                  onClick={() => setActiveFilter(k)}
                >
                  {cfg.label}  {counts[k]}
                </Button>
              );
            })}
          </HStack>
        )}

        {/* Loading */}
        {loading && (
          <Flex justify="center" align="center" py={28}>
            <VStack spacing={3}>
              <Spinner size="md" color="#508cf7" thickness="2px" />
              <Text color="gray.300" fontSize="sm" fontWeight="400">Loading…</Text>
            </VStack>
          </Flex>
        )}

        {/* Empty state */}
        {!loading && events.length === 0 && (
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Flex
              direction="column"
              align="flex-start"
              py={{ base: 10, md: 16 }}
              px={{ base: 5, md: 8 }}
              bg="white"
              borderRadius="2xl"
              border="1px dashed"
              borderColor="gray.200"
            >
              <Box
                w="56px"
                h="56px"
                borderRadius="xl"
                bg="blue.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={5}
              >
                <Icon as={IoCalendarOutline} boxSize={6} color="#508cf7" />
              </Box>
              <Text fontWeight="600" fontSize="md" color="gray.700" mb={1}>
                No events yet
              </Text>
              <Text color="gray.400" fontSize="sm" mb={6} textAlign="left" maxW="320px" lineHeight="1.6">
                Create your first event and start selling tickets on Proximy.
              </Text>
              <Button
                leftIcon={<Icon as={IoAddOutline} />}
                bg="#508cf7"
                color="white"
                borderRadius="xl"
                fontWeight="600"
                size="sm"
                _hover={{ bg: "#226cf1" }}
                onClick={handleNewEvent}
              >
                Create Your First Event
              </Button>
            </Flex>
          </MotionBox>
        )}

        {/* Filtered empty */}
        {!loading && events.length > 0 && filtered.length === 0 && (
          <Flex justify="flex-start" align="center" py={{ base: 10, md: 16 }}>
            <HStack spacing={3} align="center">
              <Icon as={IoAlertCircleOutline} boxSize={6} color="gray.200" />
              <Text color="gray.400" fontSize="sm">No events with this status.</Text>
            </HStack>
          </Flex>
        )}

        {/* Event grid */}
        {!loading && filtered.length > 0 && (
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={{ base: 3, md: 4 }}>
            {filtered.map((event, i) => (
              <EventCard
                key={event.docId || event.id}
                event={event}
                index={i}
                currentUid={user?.uid}
                onView={handleView}
                onEdit={handleEdit}
                onDashboard={setDashboardEvent}
                onRespond={requestRespond}
                responding={responding}
              />
            ))}
          </Grid>
        )}
      </Container>

      <CreateEventModal
        isOpen={isOpen}
        onClose={handleModalClose}
        user={user}
        event={editingEvent}
        onEventUpdated={fetchEvents}
      />

      <EventDashboardModal
        isOpen={!!dashboardEvent}
        onClose={() => setDashboardEvent(null)}
        event={dashboardEvent}
        onChanged={fetchEvents}
      />

      <AlertDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        leastDestructiveRef={cancelConfirmRef}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay backdropFilter="blur(4px)" bg="blackAlpha.500">
          <AlertDialogContent
            borderRadius={{ base: "xl", md: "2xl" }}
            mx={4}
            overflow="hidden"
          >
            <AlertDialogHeader
              pt={5}
              pb={2}
              px={6}
              borderBottom="1px solid"
              borderColor="gray.100"
            >
              <HStack spacing={3} align="center">
                <Box
                  bg={confirmAction?.action === "approved" ? "green.50" : "red.50"}
                  p={2}
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    as={
                      confirmAction?.action === "approved"
                        ? IoCheckmarkOutline
                        : IoCloseOutline
                    }
                    color={
                      confirmAction?.action === "approved" ? "#16a34a" : "#dc2626"
                    }
                    boxSize={4}
                  />
                </Box>
                <Box>
                  <Text fontSize="lg" fontWeight="700" color="gray.800">
                    {confirmAction?.action === "approved"
                      ? "Approve this event?"
                      : "Reject this event?"}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    This decision is final.
                  </Text>
                </Box>
              </HStack>
              <AlertDialogCloseButton top={4} right={4} />
            </AlertDialogHeader>

            <AlertDialogBody px={6} py={5}>
              <VStack align="stretch" spacing={4}>
                {confirmAction?.event && (
                  <Box
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="lg"
                    px={3}
                    py={2}
                  >
                    <Text fontSize="xs" color="gray.400" fontWeight="600">
                      Event
                    </Text>
                    <Text fontSize="sm" color="gray.700" fontWeight="600" noOfLines={1}>
                      {confirmAction.event.name ||
                        confirmAction.event.eventName ||
                        confirmAction.event.title ||
                        confirmAction.event.hangoutName ||
                        "Untitled event"}
                    </Text>
                  </Box>
                )}

                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                  Once you{" "}
                  <Text
                    as="span"
                    fontWeight="700"
                    color={
                      confirmAction?.action === "approved" ? "#16a34a" : "#dc2626"
                    }
                  >
                    {confirmAction?.action === "approved" ? "approve" : "reject"}
                  </Text>{" "}
                  this event, the host will be notified and you won't be able to
                  change your decision from this page.
                </Text>

                <HStack
                  spacing={2}
                  bg="orange.50"
                  border="1px solid"
                  borderColor="orange.200"
                  borderRadius="lg"
                  px={3}
                  py={3}
                  align="flex-start"
                >
                  <Icon
                    as={IoAlertCircleOutline}
                    color="orange.500"
                    boxSize={4}
                    mt="2px"
                    flexShrink={0}
                  />
                  <Box>
                    <Text fontSize="xs" color="orange.700" fontWeight="600">
                      Need to change it later?
                    </Text>
                    <Text fontSize="11px" color="orange.700" mt="1px">
                      Email{" "}
                      <Link
                        href="mailto:arpan@proximy.ca"
                        fontWeight="700"
                        textDecoration="underline"
                      >
                        arpan@proximy.ca
                      </Link>{" "}
                      and we'll handle it case-by-case.
                    </Text>
                  </Box>
                </HStack>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter
              borderTop="1px solid"
              borderColor="gray.100"
              py={4}
              px={6}
            >
              <Button
                ref={cancelConfirmRef}
                size="sm"
                variant="ghost"
                fontWeight="600"
                onClick={() => setConfirmAction(null)}
                mr={2}
                isDisabled={
                  !!responding &&
                  confirmAction &&
                  responding.id ===
                  (confirmAction.event.docId || confirmAction.event.id)
                }
              >
                Cancel
              </Button>
              <Button
                size="sm"
                fontWeight="600"
                borderRadius="lg"
                px={5}
                color="white"
                bg={confirmAction?.action === "approved" ? "#22c55e" : "#ef4444"}
                _hover={{
                  bg:
                    confirmAction?.action === "approved" ? "#16a34a" : "#dc2626",
                }}
                leftIcon={
                  <Icon
                    as={
                      confirmAction?.action === "approved"
                        ? IoCheckmarkOutline
                        : IoCloseOutline
                    }
                    boxSize="14px"
                  />
                }
                isLoading={
                  !!responding &&
                  confirmAction &&
                  responding.id ===
                  (confirmAction.event.docId || confirmAction.event.id) &&
                  responding.action === confirmAction.action
                }
                loadingText={
                  confirmAction?.action === "approved" ? "Approving…" : "Rejecting…"
                }
                onClick={async () => {
                  if (!confirmAction) return;
                  const { event, action } = confirmAction;
                  await handleRespond(event, action);
                  setConfirmAction(null);
                }}
              >
                {confirmAction?.action === "approved"
                  ? "Yes, approve"
                  : "Yes, reject"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
